import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { MemberChip } from "./Bits";

const Stars = ({ value, onChange, size = "h-4 w-4" }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={onChange ? `review-star-${n}` : undefined}>
        <Star className={`${size} ${n <= value ? "fill-[#F2C230] text-[#F2C230]" : "text-[#120E1F]/20"}`} />
      </button>
    ))}
  </div>
);

export const ProductReviews = ({ productId }) => {
  const { user } = useAuth();
  const { tr } = useLang();
  const [list, setList] = useState([]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const load = useCallback(() => api.get(`/products/${productId}/reviews`).then((r) => setList(r.data)), [productId]);
  useEffect(() => { load(); }, [load]);
  const mine = user && list.some((r) => r.user_id === user.id);
  const avg = list.length ? list.reduce((s, r) => s + r.rating, 0) / list.length : 0;
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/products/${productId}/reviews`, { rating, text }); setText(""); toast.success(data.points ? `+${data.points} ${tr("Punkte", "points")}` : tr("Danke!", "Thanks!")); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-6" data-testid="product-reviews">
      <div className="flex items-end gap-4"><h2 className="section-title text-3xl">{tr("Bewertungen", "Reviews")}</h2>
        {list.length > 0 && <span className="flex items-center gap-2 text-sm text-[#707783] pb-1"><Stars value={Math.round(avg)} />{avg.toFixed(1)} · {list.length}</span>}</div>
      {user && user.onboarded && !mine && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#120E1F]/5 p-5 space-y-3 max-w-2xl" data-testid="review-form">
          <div className="flex items-center justify-between"><Stars value={rating} onChange={setRating} size="h-6 w-6" /><span className="text-xs text-[#B8860B] font-semibold">+100 {tr("Punkte", "points")}</span></div>
          <textarea required minLength={3} maxLength={600} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={tr("Wie gefällt dir das Produkt?", "What do you think of this product?")} data-testid="review-text-input" className="w-full rounded-xl border border-[#120E1F]/10 px-4 py-3 text-sm focus:outline-none focus:border-[#8A5CD6]" />
          <button className="btn-dark" data-testid="review-submit-btn">{tr("Bewertung senden", "Submit review")}</button>
        </form>
      )}
      {!user && <p className="text-sm text-[#707783]"><Link to="/join" className="text-[#6E3FB8] underline">{tr("Werde Mitglied", "Become a member")}</Link> {tr("und verdiene +100 Punkte für deine Bewertung.", "and earn +100 points for your review.")}</p>}
      <div className="grid md:grid-cols-2 gap-4">
        {list.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-[#120E1F]/5 p-5 space-y-3" data-testid={`review-${r.id}`}>
            <div className="flex justify-between"><MemberChip author={r.author} /><Stars value={r.rating} /></div>
            <p className="text-sm text-[#120E1F]/80">{r.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
